import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mobilitySelfApi } from '@/api/mobility';
import type { CreateMyExpenseClaim } from '@/types/mobility';

const SELF_KEY = 'mobility-self';

/**
 * Fetches the signed-in user's mobility self-service profile.
 *
 * @returns A TanStack Query result wrapping the caller's {@link MobilitySelfProfile}.
 */
export function useMyProfile() {
  return useQuery({
    queryKey: [SELF_KEY, 'profile'],
    queryFn: () => mobilitySelfApi.getProfile(),
  });
}

/**
 * Fetches the caller's mobility account for a given year.
 *
 * Polls every 30 seconds while enabled, and always refetches on mount and
 * window focus, since the balance can change from server-side events (e.g.
 * a monthly release) that aren't otherwise reflected on this page.
 *
 * @param year - The calendar year to fetch the account for.
 * @param enabled - Whether the query should run; disable to skip fetching (and polling).
 * @returns A TanStack Query result wrapping the {@link MobilityAccount}.
 */
export function useMyAccount(year: number, enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'account', year],
    queryFn: () => mobilitySelfApi.getMyAccount(year),
    enabled,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: enabled ? 30_000 : false,
  });
}

/**
 * Fetches the caller's mobility ledger entries for a given year, polling
 * every 30 seconds while enabled to reflect server-side postings promptly.
 *
 * @param year - The calendar year to fetch ledger entries for.
 * @param enabled - Whether the query should run; disable to skip fetching (and polling).
 * @returns A TanStack Query result wrapping the ledger entries.
 */
export function useMyLedger(year: number, enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'ledger', year],
    queryFn: () => mobilitySelfApi.getMyLedger(year),
    enabled,
    refetchOnWindowFocus: 'always',
    refetchInterval: enabled ? 30_000 : false,
  });
}

/**
 * Fetches the caller's current mobility category eligibility result,
 * polling every 30 seconds while enabled.
 *
 * @param enabled - Whether the query should run; disable to skip fetching (and polling).
 * @returns A TanStack Query result wrapping the {@link EmployeeCategoryResult}.
 */
export function useMyCategory(enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'category'],
    queryFn: () => mobilitySelfApi.getMyCategory(),
    enabled,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: enabled ? 30_000 : false,
  });
}

/**
 * Fetches the caller's Pillar 3 (cash payout) contribution projection for a given year.
 *
 * @param year - The calendar year to project.
 * @param enabled - Whether the query should run; disable to skip fetching.
 * @returns A TanStack Query result wrapping the {@link PillarThreeProjection}.
 */
export function useMyPillarThreeProjection(year: number, enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'pillar-three-projection', year],
    queryFn: () => mobilitySelfApi.getMyPillarThreeProjection(year),
    enabled,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetches the catalog of expense types available for self-service claims.
 *
 * @param enabled - Whether the query should run; disable to skip fetching.
 * @returns A TanStack Query result wrapping the list of expense types.
 */
export function useExpenseTypes(enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'expense-types'],
    queryFn: () => mobilitySelfApi.getExpenseTypes(),
    enabled,
  });
}

/**
 * Fetches the home and work address of the caller's most recent woon-werk
 * claim, to prefill a new woon-werk claim.
 *
 * @param enabled - Whether the query should run; disable to skip fetching.
 * @returns A TanStack Query result wrapping the last used addresses.
 */
export function useMyCommuteAddresses(enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'commute-addresses'],
    queryFn: () => mobilitySelfApi.getMyCommuteAddresses(),
    enabled,
  });
}

/**
 * Fetches the caller's expense claims for a given year, polling every 30
 * seconds while enabled to reflect status changes (e.g. approvals) promptly.
 *
 * @param year - The calendar year to fetch claims for.
 * @param enabled - Whether the query should run; disable to skip fetching (and polling).
 * @returns A TanStack Query result wrapping the caller's claims.
 */
export function useMyClaims(year: number, enabled = true) {
  return useQuery({
    queryKey: [SELF_KEY, 'claims', year],
    queryFn: () => mobilitySelfApi.getMyClaims(year),
    enabled,
    refetchOnWindowFocus: 'always',
    refetchInterval: enabled ? 30_000 : false,
  });
}

/**
 * Mutation to create a new expense claim, invalidating the claims list on success.
 *
 * @returns A TanStack mutation accepting a {@link CreateMyExpenseClaim} payload.
 */
export function useCreateMyClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMyExpenseClaim) => mobilitySelfApi.createMyClaim(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'claims'] });
      queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'commute-addresses'] });
    },
  });
}

/**
 * Mutation to upload a supporting attachment for a claim, invalidating the
 * claims list on success so the new attachment appears.
 *
 * @returns A TanStack mutation accepting `{ claimId, file }`.
 */
export function useUploadMyAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, file }: { claimId: number; file: File }) =>
      mobilitySelfApi.uploadMyAttachment(claimId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'claims'] }),
  });
}

/**
 * Mutation to resubmit a rejected claim with revised details, invalidating
 * the claims list on success.
 *
 * @returns A TanStack mutation accepting `{ claimId, payload }`.
 */
export function useReviseMyRejectedClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ claimId, payload }: { claimId: number; payload: CreateMyExpenseClaim }) =>
      mobilitySelfApi.reviseMyRejectedClaim(claimId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'claims'] });
      queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'commute-addresses'] });
    },
  });
}

/**
 * Mutation to submit a draft claim for approval, invalidating the claims list on success.
 *
 * @returns A TanStack mutation accepting a claim id.
 */
export function useSubmitMyClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (claimId: number) => mobilitySelfApi.submitMyClaim(claimId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'claims'] }),
  });
}

/**
 * Mutation to cancel a claim. On success, invalidates claims, ledger, and
 * account queries, since cancelling a processed claim can reverse ledger
 * entries and change the account balance.
 *
 * @returns A TanStack mutation accepting a claim id.
 */
export function useCancelMyClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (claimId: number) => mobilitySelfApi.cancelMyClaim(claimId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'claims'] });
      void queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'ledger'] });
      void queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'account'] });
    },
  });
}

/**
 * Mutation to opt into yearly (lump-sum) budget release for a given year.
 * On success, invalidates the account and ledger queries to reflect the new release.
 *
 * @returns A TanStack mutation accepting the target year.
 */
export function useSelectYearlyRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (year: number) => mobilitySelfApi.selectYearlyRelease(year),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'account'] });
      void queryClient.invalidateQueries({ queryKey: [SELF_KEY, 'ledger'] });
    },
  });
}
